import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AccortoService,
  DataRecord,
  DataTable,
  DataType,
  FormManager,
  Logger,
  ModelUtil,
  NotificationService,
  UiFormField,
  UiFormSection,
  UiTab
} from 'accorto';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { TrackItemService } from '../track-item/track-item.service';
import { PerDiemItem } from '../model/per-diem-item';
import { CResponseTrack } from '../model/c-response-track';
import { FkInfo } from '../../../../accorto/src/lib/model/fk-info';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { TEItemUtil } from '../model/t-e-item-util';

/**
 * German Per Diem
 */
@Component({
  selector: 't4d-per-diem-de',
  templateUrl: './per-diem-de.component.html',
  styleUrls: [ './per-diem-de.component.scss' ]
})
export class PerDiemDeComponent implements OnInit, OnDestroy {

  message: string;
  error: string;
  busy: boolean = false;

  // Query
  tripFm: FormManager;
  tripStartDate: UiFormField;
  tripEndDate: UiFormField;
  tripDestination: UiFormField;
  tripRecord: DataRecord = new DataRecord();
  tripOptions: FkInfo[] = [];

  // Result
  items: PerDiemItem[] = [];
  diemForm: FormGroup;
  diemDays: FormArray;

  totals: number;
  totalMeals: number;
  totalMeals2: number;
  totalBreakfast: number;
  totalLunch: number;
  totalDinner: number;
  totalIncidentals: number;
  totalLodging: number;

  ui: UiTab = new UiTab();
  record: DataRecord = new DataRecord();
  /** Child Form */
    // @ViewChild(FormComponent, { static: false }) theForm: FormComponent;

  private log: Logger = new Logger('PerDiemDe');
  private subscriptions: Subscription[] = [];

  /**
   * German Per Diem
   */
  constructor(private fb: FormBuilder,
              private router: Router,
              private route: ActivatedRoute,
              private conf: AccortoService,
              private service: TrackItemService,
              private notify: NotificationService) {
    // Get destinations
    this.message = 'querying ...';
    const item = new PerDiemItem();
    item.type = 'de';
    this.service.queryPerDiem(item)
      .subscribe((response: CResponseTrack) => {
        this.message = response.message;
        this.error = response.error;
        if (response.perDiemItems && response.perDiemItems.length > 0) {
          const allOptions: string[] = response.perDiemItems[ 0 ].destinationOptions;
          this.tripOptions = [];
          for (const op of allOptions) {
            this.tripOptions.push(new FkInfo().init(op));
          }
        }
        // this.log.debug('destinations', this.tripOptions)();
      });

    // Query Form
    const table = new DataTable();
    table.name = 'trip';
    const ui = new UiTab();
    ui.name = table.name;
    ui.dataTable = table;
    const formSection = new UiFormSection();
    ui.formSectionList.push(formSection);

    ModelUtil.newDataColumn(table, 'startDate', DataType.DATE, 'Start', true);
    ModelUtil.newDataColumn(table, 'endDate', DataType.DATE, 'Ende', true);
    ModelUtil.newDataColumn(table, 'destination', DataType.LIST, 'Ziel', true);

    this.tripStartDate = ModelUtil.cloneUiFormField(table, 'startDate');
    formSection.uiFormFieldList.push(this.tripStartDate);
    this.tripEndDate = ModelUtil.cloneUiFormField(table, 'endDate');
    formSection.uiFormFieldList.push(this.tripEndDate);
    this.tripDestination = ModelUtil.cloneUiFormField(table, 'destination');
    // this.tripDestination.description = 'Zip code';
    formSection.uiFormFieldList.push(this.tripDestination);
    //
    this.tripRecord.rowNo = 0;
    this.tripFm = new FormManager(fb, ui, this.tripRecord, undefined, conf, undefined, undefined, undefined);

    // Per Diem Form
    this.diemForm = this.fb.group({
      days: this.fb.array([])
    });
    this.diemDays = this.diemForm.get('days') as FormArray;
    this.diemForm.valueChanges.subscribe((v) => {
      this.log.debug('diemForm', v)();
      this.calculate(v);
    });
  } // constructor

  isDebug(): boolean {
    return this.conf.isDebug;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => {
      sub.unsubscribe();
    });
    this.subscriptions = [];
  }

  ngOnInit(): void {
    // ui
    this.subscriptions.push(this.route.data.subscribe((data => {
      if (data.uiTab) {
        this.log.debug('uiTab', data.uiTab)();
        this.ui = TEItemUtil.cloneUi(data.uiTab, 'ref',
          true, this.conf.session.settings);
        this.record = ModelUtil.newDataRecord(this.ui);
      }
    })));
  } // ngOnInit

  /**
   * Create Per Diems
   */
  onDiemSubmit() {
    this.log.info('onDiemSubmit', this.items, this.record)();
    this.message = 'creating ...';
    this.error = undefined;
    this.busy = true;
    this.service.perDiemExpenses(this.items, this.record).subscribe((response: CResponseTrack) => {
      this.message = response.message;
      this.error = response.error;
      this.busy = false;
      if (!this.error) {
        this.notify.addSuccess(response.message);
        const pp: Params = {
          startTimeMs: response.startTimeMs,
          endTimeMs: response.endTimeMs
        };
        this.router.navigate([ '/expense-table' ],
          { queryParams: pp });
      }
    });
  } // onDiemSubmit

  onNew() {
    this.log.info('onNew')();
    this.tripFm.formGroup.reset();
    this.items = [];
  }

  /**
   * Submit Trip Query
   */
  onTripSubmit() {
    this.error = undefined;
    this.message = 'querying ...';
    this.busy = true;

    const item = new PerDiemItem();
    item.type = 'de';
    item.destination = this.tripRecord.changeMap.destination;
    item.dateMs = Number(this.tripRecord.changeMap.startDate);
    item.endDateMs = Number(this.tripRecord.changeMap.endDate);
    this.log.info('onTripSubmit', item)();
    this.service.queryPerDiem(item)
      .subscribe((result: CResponseTrack) => {
        this.error = result.error;
        this.message = result.message;
        this.items = result.perDiemItems;
        this.buildForm();
        this.busy = false;
      });
  } // onTripSubmit

  /**
   * Build Per Diem Form
   */
  private buildForm() {
    this.diemDays.clear();
    this.items.forEach((item: PerDiemItem) => {
      item.isMeals = true;
      item.isMeals2 = false;
      item.isBreakfast = false;
      item.isLunch = false;
      item.isDinner = false;
      item.isIncidental = false;
      item.isLodging = true;
      this.diemDays.push(this.buildFormDay(item));
    });
  } // buildForm

  /**
   * build per diem day
   */
  private buildFormDay(item: PerDiemItem): FormGroup {
    return this.fb.group({
      meals: [ item.isMeals ],
      meals2: [ item.isMeals2 ],
      breakfast: [ item.isBreakfast ],
      lunch: [ item.isLunch ],
      dinner: [ item.isDinner ],
      incidental: [ item.isIncidental ],
      lodging: [ item.isLodging ],
      description: [ '' ]
    });
  } // buildFormDay

  /**
   * Calculate total(s)
   */
  private calculate(v) {
    this.totalMeals = 0;
    this.totalMeals2 = 0;
    this.totalBreakfast = 0;
    this.totalLunch = 0;
    this.totalDinner = 0;
    this.totalIncidentals = 0;
    this.totalLodging = 0;

    for (let i = 0; i < this.diemDays.length; i++) {
      const item: PerDiemItem = this.items[ i ];
      const group = this.diemDays.at(i) as FormGroup;
      const values = group.value;
      // this.log.debug('calculate ' + i, item, values)();

      if (values.meals && !item.isMeals) { // meals changed
        group.patchValue({
          meals2: false
        }, { emitEvent: false });
        values.meals2 = false;
        item.rateBreakfast = item.rateMeals * -0.2;
        item.rateLunch = item.rateMeals * -0.4;
        item.rateDinner = item.rateMeals * -0.4;
      } else if (values.meals2 && !item.isMeals2) { // meals2 changed
        group.patchValue({
          meals: false
        }, { emitEvent: false });
        values.meals = false;
        item.rateBreakfast = item.rateMeals2 * -0.2;
        item.rateLunch = item.rateMeals2 * -0.4;
        item.rateDinner = item.rateMeals2 * -0.4;
      }

      // calculate
      item.isMeals = values.meals;
      if (item.isMeals) {
        this.totalMeals += (item.rateMeals ? item.rateMeals : 0);
      }
      item.isMeals2 = values.meals2;
      if (item.isMeals2) {
        this.totalMeals2 += (item.rateMeals2 ? item.rateMeals2 : 0);
      }
      item.isBreakfast = values.breakfast;
      if (item.isBreakfast) {
        this.totalBreakfast += (item.rateBreakfast ? item.rateBreakfast : 0);
      }
      item.isLunch = (values.lunch);
      if (item.isLunch) {
        this.totalLunch += (item.rateLunch ? item.rateLunch : 0);
      }
      item.isDinner = (values.dinner);
      if (item.isDinner) {
        this.totalDinner += (item.rateDinner ? item.rateDinner : 0);
      }
      item.isIncidental = values.incidental;
      if (item.isIncidental) {
        this.totalIncidentals += (item.rateIncidental ? item.rateIncidental : 0);
      }
      item.isLodging = values.lodging;
      if (item.isLodging) {
        this.totalLodging += (item.rateLodging ? item.rateLodging : 0);
      }
      item.description = values.description;
    }
    //
    this.totals = this.totalMeals + this.totalMeals2
      + this.totalBreakfast + this.totalLunch + this.totalDinner
      + this.totalIncidentals + this.totalLodging;
  } // calculate

} // PerDiemDeComponent
